import type { BlockType } from "./blocks";
import type { PublicEvent, PublicForm, PublicGroup } from "./public-site";
import type { PublicProduct } from "./shop";
import type { GuestCheckout } from "./checkout";

/*
 * Example content for every block type, as an editor would save it. Two suites read it: the contract tests in
 * `blocks.test.ts`, which check that each example parses, and the theme conformance test in `apps/public-web`
 * (ADR 0050 section 5), which draws each example in every theme and looks for its words in the markup. One set, so a
 * field a block gains is exercised by both the day it is added here.
 *
 * The examples fill the optional text fields a renderer prints, because a field the example leaves out is a field the
 * conformance test cannot see go missing.
 */

const church = "0b6b1d2e-6f0a-4c61-9d2f-3f1f4c1a2b3c";
const image = { path: `${church}/media/sunday.jpg`, alt: "The congregation singing" };
/** The video hero's film, beside its poster (`image`). */
const video = `${church}/media/sunday.mp4`;
/** The form the two form blocks point at; `EXAMPLE_FORMS` holds it under this id. */
export const EXAMPLE_FORM_ID = "5f7c1a3e-2b4d-4e6f-8a9b-0c1d2e3f4a5b";
const para = [{ type: "paragraph", content: [{ text: "Yes, behind the hall." }] }];

/** One valid example of every type, without its `type`; `{ type, ...BLOCK_EXAMPLES[type] }` is the block. */
export const BLOCK_EXAMPLES: Readonly<Record<BlockType, Readonly<Record<string, unknown>>>> = {
  hero: {
    headline: "Welcome",
    subheadline: "A church for the whole city.",
    primaryAction: { label: "Visit", href: "/plan-a-visit" },
    secondaryAction: { label: "Watch online", href: "/watch" },
    image,
    video,
  },
  service_times: {
    heading: "Times",
    services: [
      { name: "Worship", day: "sunday", time: "10:00", note: "Kids church runs alongside" },
    ],
  },
  locations: {
    heading: "Find us",
    locations: [
      {
        name: "Main",
        addressLines: ["1 Church St"],
        phone: "555 0100",
        mapUrl: "https://maps.example.org/main",
      },
    ],
  },
  mission: {
    heading: "Our mission",
    statement: "To love God and our neighbours.",
    values: [{ title: "Welcome", description: "Everyone has a seat." }],
  },
  sermon_feature: { heading: "Latest message", source: { mode: "latest" }, limit: 2 },
  devotional_feature: { heading: "Today's devotional" },
  event_listing: { heading: "Coming up", emptyMessage: "Nothing yet." },
  group_finder: { heading: "Find a group", filters: ["day", "location"] },
  ministry_grid: {
    heading: "Ministries",
    items: [
      { title: "Kids", description: "Sundays at ten.", link: { label: "More", href: "/kids" } },
    ],
  },
  staff_grid: {
    heading: "Our team",
    people: [
      {
        name: "Ada Obi",
        role: "Pastor",
        bio: "Ada has led the church since 2019.",
        email: "ada@example.org",
      },
    ],
  },
  giving: {
    heading: "Give",
    message: "Every gift supports the food bank.",
    action: { label: "Give online", href: "https://give.example.org" },
  },
  store_collection: { heading: "Books", collectionId: "9a8b7c6d-5e4f-4a3b-8c2d-1e0f9a8b7c6d" },
  newsletter: {
    heading: "Stay in touch",
    description: "One email a week.",
    formId: EXAMPLE_FORM_ID,
  },
  contact_form: {
    heading: "Contact us",
    description: "We reply within a day.",
    formId: EXAMPLE_FORM_ID,
  },
  plan_visit: {
    heading: "Your first Sunday",
    intro: "Here is what to expect.",
    steps: [{ title: "Park", description: "Behind the hall." }, { title: "Come in" }],
    action: { label: "Tell us you are coming", href: "/contact" },
  },
  rich_text: {
    body: [
      { type: "heading", level: 2, content: [{ text: "About" }] },
      {
        type: "paragraph",
        content: [
          { text: "We are ", italic: true },
          { text: "here", href: "/about" },
        ],
      },
      { type: "list", items: [[{ text: "One" }], [{ text: "Two" }]] },
      { type: "quote", content: [{ text: "Be still" }], cite: "Psalm 46" },
    ],
  },
  faq: { heading: "Questions", items: [{ question: "Is there parking?", answer: para }] },
  media_gallery: { heading: "Easter morning", images: [{ image, caption: "Easter" }] },
  custom_embed: {
    provider: "youtube",
    url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    title: "Sunday service recording",
    caption: "Last Sunday",
  },
  livestream: {
    heading: "Watch online",
    provider: "youtube",
    url: "https://www.youtube.com/channel/UCabcdefghijklmnopqrstuv/live",
    services: [
      { day: "sunday", start: "09:00", durationMinutes: 90 },
      { day: "sunday", start: "11:00", durationMinutes: 90 },
    ],
    replayUrl: "/sermons",
    upcomingMessage: "Join us online every Sunday.",
  },
};

/** A public group, for the group finder's example. */
export const EXAMPLE_GROUPS: readonly PublicGroup[] = [
  {
    id: "group-1",
    name: "Tuesday Night",
    slug: "tuesday-night",
    description: "Bible study over dinner.",
    meetingDay: "tuesday",
    meetingTime: "19:00",
    joiningRule: "open",
    isFull: false,
    leaders: ["Lee Leader"],
  },
];

/** A public event with one date that takes registrations, for the event listing's example. */
export const EXAMPLE_EVENTS: readonly PublicEvent[] = [
  {
    id: "event-1",
    title: "Sunday worship",
    slug: "sunday-worship",
    description: "Everyone is welcome.",
    location: "The main hall",
    registration: "open",
    occurrences: [
      {
        id: "occurrence-1",
        startsAt: "2026-10-04T17:00:00Z",
        endsAt: "2026-10-04T18:30:00Z",
        isFull: false,
      },
    ],
  },
];

/** The form both form blocks point at. */
export const EXAMPLE_FORMS: Readonly<Record<string, PublicForm>> = {
  [EXAMPLE_FORM_ID]: {
    name: "Contact",
    token: "example-token",
    fields: [
      { key: "name", label: "Your name", type: "text", required: true },
      { key: "email", label: "Email", type: "email", required: true },
      // A text-message consent box, so the carrier-reviewed words beside it are drawn and checked in every theme.
      {
        key: "texts",
        label: "Text me reminders",
        type: "consent",
        required: false,
        channel: "sms",
      },
    ],
  },
};

/**
 * The products a store collection block and the Shop page draw (ADR 0071): one with a picture and one price, one in
 * three sizes at two prices with one size sold out, and one sold out altogether.
 */
export const EXAMPLE_PRODUCTS: readonly PublicProduct[] = [
  {
    id: "7d1e2f30-4a5b-4c6d-8e7f-90a1b2c3d4e5",
    name: "Morning devotional",
    description: "Fifty-two short readings for the year, one for each week.",
    currency: "usd",
    image: { path: "alpha/media/devotional.jpg", alt: "The devotional's blue cover" },
    fulfilment: "either",
    variants: [
      {
        id: "a1b2c3d4-0001-4000-8000-000000000001",
        name: "Standard",
        priceCents: 1500,
        available: true,
      },
    ],
  },
  {
    id: "8e2f3041-5b6c-4d7e-9f80-a1b2c3d4e5f6",
    name: "Youth retreat shirt",
    currency: "usd",
    variants: [
      {
        id: "a1b2c3d4-0002-4000-8000-000000000001",
        name: "Small",
        priceCents: 2000,
        available: false,
      },
      {
        id: "a1b2c3d4-0002-4000-8000-000000000002",
        name: "Medium",
        priceCents: 2000,
        available: true,
      },
      { id: "a1b2c3d4-0002-4000-8000-000000000003", name: "XL", priceCents: 2500, available: true },
    ],
  },
  {
    id: "9f304152-6c7d-4e8f-a091-b2c3d4e5f6a7",
    name: "Church mug",
    description: "Holds a full cup of coffee.",
    currency: "usd",
    variants: [{ name: "Standard", priceCents: 1200, available: false }],
  },
];

/** Where the example products' Buy link goes: a church's member portal Shop on HoW's own address. */
export const EXAMPLE_SHOP_URL = "https://alpha.members.how.example.org/shop";

/** A paid event with an add-on, for a guest's payment form (ADR 0076). */
export const EXAMPLE_PAID_EVENT: PublicEvent = {
  id: "event-paid",
  title: "Youth retreat",
  slug: "youth-retreat",
  description: "A weekend away for grades 6 to 12.",
  registration: "open",
  priceCents: 2500,
  currency: "usd",
  addOns: [
    {
      id: "addon-lunch",
      name: "Retreat lunch",
      variants: [
        {
          id: "a1b2c3d4-0003-4000-8000-000000000001",
          name: "Standard",
          priceCents: 800,
          available: true,
        },
      ],
    },
  ],
  occurrences: [
    {
      id: "0c0c0c0c-0000-4000-8000-000000000001",
      startsAt: "2026-11-07T16:00:00Z",
      endsAt: "2026-11-08T22:00:00Z",
      isFull: false,
    },
  ],
};

/** Guest payment on the example site (ADR 0076): a church that takes cards and offers pickup and shipping. */
export const EXAMPLE_GUEST_CHECKOUT: GuestCheckout = {
  action: "https://functions.example.org/guest-checkout",
  payments: true,
  token: `${"a".repeat(64)}.1791212400`,
  store: {
    pickup: true,
    pickupNote: "At the welcome desk after any service.",
    shipping: true,
    shippingRateCents: 500,
    freeShippingOverCents: 5000,
    shipCountries: ["US"],
    tax: false,
  },
  signInUrl: "https://alpha.members.how.example.org/events",
  outcome: null,
  error: null,
  occurrenceId: null,
};
